import React, { createContext, useState, useContext, useEffect } from 'react';
import axios from 'axios';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  // Use environment variable or fallback to current origin
  const API_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;

  // Check if user is already authenticated on mount
  // CRITICAL: If returning from OAuth callback, skip the /me check.
  // AuthCallback will exchange the session_id and establish the session first.
  useEffect(() => {
    if (window.location.hash?.includes('session_id=')) {
      setLoading(false);
      return;
    }
    checkAuth();
  }, []);

  const checkAuth = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/auth/session`, {
        withCredentials: true
      });
      
      if (response.data.authenticated) {
        setUser(response.data.user);
        setIsAuthenticated(true);
      } else {
        setUser(null);
        setIsAuthenticated(false);
      }
    } catch (error) {
      console.error('[AuthContext] Auth check failed:', error);
      setUser(null);
      setIsAuthenticated(false);
    } finally {
      setLoading(false);
    }
  };

  const signup = async (userData) => {
    try {
      const response = await axios.post(`${API_URL}/api/auth/signup`, userData);
      return { success: true, user: response.data };
    } catch (error) {
      const message = error.response?.data?.detail || 'Signup failed';
      return { success: false, error: message };
    }
  };

  const login = async (credentials) => {
    try {
      const response = await axios.post(`${API_URL}/api/auth/login`, credentials, {
        withCredentials: true
      });
      
      // After login, check auth to get user data
      await checkAuth();
      return { success: true };
    } catch (error) {
      const message = error.response?.data?.detail || 'Login failed';
      return { success: false, error: message };
    }
  };

  const processGoogleSession = async (sessionId) => {
    try {
      console.log('Processing Google session with ID:', sessionId);
      const response = await axios.post(
        `${API_URL}/api/auth/oauth/process-session`,
        {},
        {
          headers: { 'X-Session-ID': sessionId },
          withCredentials: true
        }
      );
      
      console.log('OAuth response:', response.data);
      setUser(response.data.user);
      setIsAuthenticated(true);
      
      // Note: checkAuth() will be called on next page load/mount
      // Calling it here can cause race conditions with cookie propagation
      
      return { success: true, user: response.data.user };
    } catch (error) {
      console.error('Google OAuth failed:', error);
      console.error('Error details:', error.response?.data);
      return { success: false, error: error.response?.data?.detail || 'Google authentication failed' };
    }
  };

  const logout = async () => {
    try {
      await axios.post(`${API_URL}/api/auth/logout`, {}, {
        withCredentials: true
      });
      setUser(null);
      setIsAuthenticated(false);
      return { success: true };
    } catch (error) {
      console.error('Logout failed:', error);
      return { success: false, error: 'Logout failed' };
    }
  };

  const updateProfile = async (profileData) => {
    try {
      const response = await axios.put(`${API_URL}/api/auth/profile`, profileData, {
        withCredentials: true
      });
      setUser(response.data);
      return { success: true, user: response.data };
    } catch (error) {
      const message = error.response?.data?.detail || 'Profile update failed';
      return { success: false, error: message };
    }
  };

  const value = {
    user,
    loading,
    isAuthenticated,
    signup,
    login,
    logout,
    checkAuth,
    processGoogleSession,
    updateProfile
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
